/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type PlanGoal } from '../../lib/api/itp.js';
import { type CreateTemplateInput } from '../../lib/api/templates.js';
import { useCreateTemplate } from '../../lib/queries/templates.js';

interface SaveTemplateModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly defaultName: string;
  readonly programType: string | null;
  readonly goals: readonly PlanGoal[];
  readonly onSaved: () => void;
}

/** Map the current plan's goals into the template create payload (P1-15b). */
function toTemplateGoals(goals: readonly PlanGoal[]): CreateTemplateInput['goals'] {
  return goals.map((g) => ({
    title: g.title,
    ...(g.category !== null ? { category: g.category } : {}),
    ...(g.measurementMethod !== null ? { measurementMethod: g.measurementMethod } : {}),
    ...(g.baseline !== null ? { baseline: g.baseline } : {}),
    ...(g.target !== null ? { target: g.target } : {}),
    ...(g.approachTags.length > 0 ? { approachTags: [...g.approachTags] } : {}),
    ...(g.steps.length > 0 ? { steps: g.steps.map((s) => ({ title: s.title })) } : {}),
  }));
}

/** "Save as template" dialog — snapshots a plan's goals into a reusable template. */
export function SaveTemplateModal({
  open,
  onOpenChange,
  defaultName,
  programType,
  goals,
  onSaved,
}: SaveTemplateModalProps): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateTemplate();
  const [name, setName] = useState(defaultName);
  const onName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const save = useCallback(() => {
    if (name.trim().length < 2) {
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        ...(programType !== null ? { programType } : {}),
        goals: toTemplateGoals(goals),
      },
      { onSuccess: onSaved },
    );
  }, [create, name, programType, goals, onSaved]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('therapist.plan.saveTemplateTitle')}
      description={t('therapist.plan.saveTemplateSubtitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="template-name"
            className="mb-1.5 block text-sm font-medium text-slate-700"
          >
            {t('therapist.plan.templateName')}
          </label>
          <input
            id="template-name"
            type="text"
            value={name}
            onChange={onName}
            className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
          />
        </div>
        {create.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('common.saveError')}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onSaved}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={save} disabled={create.isPending}>
            {t('therapist.plan.saveAsTemplate')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
